<div class="bench-overview__header">
  <div>
    <h1 class="bench-overview__title">{{ 'benchOverview.title' | translate }}</h1>
  </div>
  <div class="bench-overview__header-actions">
    <button class="bench-overview__meeting-btn" (click)="openMeetingMode()">
      <mat-icon fontIcon="groups"></mat-icon>
      {{ 'meetingMode.button' | translate }}
    </button>
    <button class="bench-overview__salesview-btn" (click)="openSalesView()">
      <mat-icon fontIcon="request_quote"></mat-icon>
      {{ 'salesView.button' | translate }}
    </button>
    <button class="bench-overview__sales-btn"
            [class.bench-overview__sales-btn--absent]="absentSalesCount > 0" (click)="openSalesModal()">
      <mat-icon fontIcon="swap_horiz"></mat-icon>
      {{ 'benchSales.button' | translate }}
      @if (absentSalesCount > 0) {
        <span class="bench-overview__sales-btn-badge">
          {{ 'benchSales.absentBadge' | translate: {count: absentSalesCount} }}
        </span>
      }
    </button>
  </div>
</div>

<div class="bench-overview__filter-bar bench-filters">
  <div class="bf-row bf-row-top">
    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="bench-overview__search bench-search">
      <mat-label>{{ 'benchOverview.filter.search' | translate }}</mat-label>
      <input matInput [(ngModel)]="searchText" (ngModelChange)="onSearchChange()" type="search">
      <mat-icon matSuffix fontIcon="search"></mat-icon>
    </mat-form-field>

    <div class="bench-overview__company-chips firma-chips">
      <button class="bench-overview__fchip"
              [ngClass]="{'bench-overview__fchip--on': selectedCompany === 'ALL'}"
              (click)="selectCompany('ALL')">
        {{ 'benchOverview.filter.allCompanies' | translate }}
      </button>
      @for (company of companies; track company) {
        <button class="bench-overview__fchip"
                [ngClass]="{ 'bench-overview__fchip--on': selectedCompany === company && !chipStyle(company, true)['background'] }"
                [style]="chipStyle(company, selectedCompany === company)"
                [matTooltip]="companyName(company)"
                (click)="selectCompany(company)">
          {{ company }}
        </button>
      }
    </div>

    <div class="bench-overview__view-toggle view-toggle">
      <button mat-icon-button [ngClass]="{'bench-overview__view-toggle--active': viewMode === 'table'}"
              (click)="setViewMode('table')" [attr.aria-label]="'benchOverview.viewTable' | translate"
              [matTooltip]="'benchOverview.viewTable' | translate">
        <mat-icon>table_rows</mat-icon>
      </button>
      <button mat-icon-button [ngClass]="{'bench-overview__view-toggle--active': viewMode === 'card'}"
              (click)="setViewMode('card')" [attr.aria-label]="'benchOverview.viewCard' | translate"
              [matTooltip]="'benchOverview.viewCard' | translate">
        <mat-icon>grid_view</mat-icon>
      </button>
    </div>
  </div>

  <div class="bf-row bf-row-selects">
    <app-ci-select
      class="bench-overview__filter-select bf-sel"
      variant="outline"
      [items]="typeFilterOptions"
      [displayWith]="displayTypeFilter"
      [label]="'benchOverview.filter.typeLabel' | translate"
      [ariaLabel]="'benchOverview.filter.typeLabel' | translate"
      [searchPlaceholder]="'ciSelect.search' | translate"
      [searchClearLabel]="'ciSelect.clearSearch' | translate"
      [emptyLabel]="'ciSelect.noMatch' | translate"
      [hitTemplate]="'ciSelect.hits' | translate"
      [(ngModel)]="selectedType"
      (ngModelChange)="applyFilter()"
    ></app-ci-select>
  </div>
</div>

<div class="bench-overview__quick-filters">
  <span class="bench-overview__qf-label">
    <mat-icon class="bench-overview__qf-icon" fontIcon="filter_alt"></mat-icon>
    {{ 'benchOverview.filter.quickLabel' | translate }}
  </span>
  @for (qf of statusFilters; track qf.key) {
    <button class="bench-overview__quick-chip"
            [ngClass]="activeQuickFilters.has(qf.key)
              ? ['bench-overview__quick-chip--active', 'bench-overview__quick-chip--active-' + qf.key.toLowerCase()]
              : []"
            (click)="toggleQuickFilter(qf.key)">
      {{ qf.labelKey | translate }}
    </button>
  }
  <span class="bench-overview__qf-sep"></span>
  <span class="bench-overview__qf-avail-group" role="group"
        [attr.aria-label]="'benchOverview.availability.label' | translate">
    <span class="bench-overview__qf-avail-label">{{ 'benchOverview.availability.label' | translate }}</span>
    @for (av of availabilityOptions; track av.key) {
      <button class="bench-overview__quick-chip"
              [class.bench-overview__quick-chip--active]="!availableFrom && availability === av.key"
              [attr.aria-pressed]="!availableFrom && availability === av.key"
              (click)="setAvailability(av.key)">
        {{ av.labelKey | translate }}
      </button>
    }
    <app-available-from-filter [value]="availableFrom" (valueChange)="onAvailableFromChange($event)"></app-available-from-filter>
  </span>
  @if (hasNonDefaultQuickFilter) {
    <button class="bench-overview__reset-link qf-reset" (click)="resetQuickFilter()">
      {{ 'benchOverview.filter.reset' | translate }}
    </button>
  }
</div>

@if (isLoading) {
  <mat-spinner mode="indeterminate" class="bench-overview__loading"></mat-spinner>
} @else {
  <div class="bench-overview__summary">
    <span class="bench-overview__summary-count">
      {{ filteredEntries.length }} {{ (filteredEntries.length === 1 ? 'benchOverview.summaryEntry' : 'benchOverview.summaryEntries') | translate }}
    </span>
    <span class="bench-overview__summary-meta">
      {{ 'benchOverview.summaryMeta' | translate: {consultants: consultantCount, applicants: applicantCount, freelancers: freelancerCount} }}
    </span>
  </div>

  @if (viewMode === 'table') {
    <div class="bench-overview__table-wrapper" #entriesScroll [style.--bench-entry-rows]="visibleRowCount">
      <table mat-table [dataSource]="filteredEntries" class="bench-overview__table">

        <ng-container matColumnDef="person">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('person')"
              (click)="onSort('person')" (keydown.enter)="onSort('person')">
            {{ 'benchOverview.column.person' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'person'">{{ sortIndicator('person') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            <div class="bench-overview__person-cell">
              @if (entry.consultantId && entry.photoId) {
                <img class="bench-overview__avatar bench-overview__avatar--photo"
                     [lazyImage]="entry.consultantId" [lazyImagePhotoId]="entry.photoId"
                     [alt]="entry.fullName"/>
              } @else {
                <span class="bench-overview__avatar">{{ getInitials(entry.fullName) }}</span>
              }
              <span class="bench-overview__person-name">
                {{ entry.fullName }}
                @if (entry.abbreviation) {
                  <span class="bench-overview__kuerzel">{{ entry.abbreviation }}</span>
                }
              </span>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="type">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('type')"
              (click)="onSort('type')" (keydown.enter)="onSort('type')">
            {{ 'benchOverview.column.type' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'type'">{{ sortIndicator('type') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @for (category of categoriesOf(entry); track category) {
              <span class="bench-overview__type-badge"
                    [ngClass]="'bench-overview__type-badge--' + category.toLowerCase()">
                {{ ('benchOverview.type.' + category.toLowerCase()) | translate }}
              </span>
            } @empty {
              <span class="bench-overview__empty">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="company">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('company')"
              (click)="onSort('company')" (keydown.enter)="onSort('company')">
            {{ 'benchOverview.column.company' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'company'">{{ sortIndicator('company') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @if (isBenchFirm(entry.firm)) {
              <app-company-badge [abbreviation]="entry.firm"></app-company-badge>
            } @else {
              <span class="bench-overview__empty">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="position">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('position')"
              (click)="onSort('position')" (keydown.enter)="onSort('position')">
            {{ 'benchOverview.column.position' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'position'">{{ sortIndicator('position') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @if (entry.position) {
              <span class="bench-overview__pos" [matTooltip]="entry.position">{{ entry.position }}</span>
            } @else {
              <span class="bench-overview__empty">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="location">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('location')"
              (click)="onSort('location')" (keydown.enter)="onSort('location')">
            {{ 'benchOverview.column.location' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'location'">{{ sortIndicator('location') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @if (entry.location) {
              <div class="bench-overview__location-cell">
                <mat-icon class="bench-overview__pin-icon" fontIcon="location_on"></mat-icon>
                {{ entry.location }}
              </div>
            } @else {
              —
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="availableFrom">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('availableFrom')"
              (click)="onSort('availableFrom')" (keydown.enter)="onSort('availableFrom')">
            {{ 'benchOverview.column.availableFrom' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'availableFrom'">{{ sortIndicator('availableFrom') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @if (entry.availableFrom) {
              <span class="bench-overview__avail"
                    [ngClass]="'bench-overview__avail--' + availabilityBucket(entry)">{{ entry.availableFrom | date: 'dd.MM.yyyy' }}</span>
            } @else {
              <span class="bench-overview__empty">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="confirmedFrom">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('confirmedFrom')"
              (click)="onSort('confirmedFrom')" (keydown.enter)="onSort('confirmedFrom')">
            {{ 'benchOverview.column.confirmedFrom' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'confirmedFrom'">{{ sortIndicator('confirmedFrom') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @if (acceptedOfferDate(entry); as confirmed) {
              {{ confirmed | date: 'dd.MM.yyyy' }}
            } @else {
              <span class="bench-overview__empty">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="vacation">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('vacation')"
              (click)="onSort('vacation')" (keydown.enter)="onSort('vacation')">
            {{ 'benchOverview.column.vacation' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'vacation'">{{ sortIndicator('vacation') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @if (!isApplicant(entry) && entry.remainingVacationDays != null) {
              {{ entry.remainingVacationDays }}
            } @else {
              <span class="bench-overview__empty">—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="offers">
          <th mat-header-cell *matHeaderCellDef class="bench-overview__th" tabindex="0"
              [attr.aria-sort]="ariaSort('offers')"
              (click)="onSort('offers')" (keydown.enter)="onSort('offers')">
            {{ 'benchOverview.column.offers' | translate }}
            <span class="bench-overview__sort" [class.bench-overview__sort--active]="sortKey === 'offers'">{{ sortIndicator('offers') }}</span>
          </th>
          <td mat-cell *matCellDef="let entry">
            @let sum = offerSummary(entry);
            <div class="bench-overview__ang-mini">
              <div class="bench-overview__ang-stats">
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--open]="sum.open > 0">
                  <b>{{ sum.open }}</b> {{ 'benchOverview.offersMini.open' | translate }}</span>
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--feedback]="sum.feedback > 0">
                  <b>{{ sum.feedback }}</b> {{ 'benchOverview.offersMini.feedback' | translate }}</span>
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--rejected]="sum.rejected > 0">
                  <b>{{ sum.rejected }}</b> {{ 'benchOverview.offersMini.rejected' | translate }}</span>
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--accepted]="sum.accepted > 0">
                  <b>{{ sum.accepted }}</b> {{ 'benchOverview.offersMini.accepted' | translate }}</span>
                <span class="bench-overview__ang-stat"
                      [class.bench-overview__ang-stat--project-created]="sum.projectCreated > 0">
                  <b>{{ sum.projectCreated }}</b> {{ 'benchOverview.offersMini.projectCreated' | translate }}</span>
              </div>
              <div class="bench-overview__ang-iv">
                <span>{{ 'benchOverview.offersMini.pdl' | translate }} <b>{{ sum.pdl }}</b></span>
                <span>{{ 'benchOverview.offersMini.customer' | translate }} <b>{{ sum.customer }}</b></span>
              </div>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"
            class="bench-overview__row" (click)="openDetail(row)"></tr>
      </table>
    </div>
  }

  @if (viewMode === 'card') {
    <div class="bench-overview__card-grid" #entriesScroll>
      @for (entry of filteredEntries; track entry.id) {
        <div class="bench-overview__card"
             [style.border-top-color]="firmColor(entry.firm)"
             (click)="openDetail(entry)">
          <div class="bench-overview__card-header">
            @if (entry.consultantId && entry.photoId) {
              <img class="bench-overview__avatar bench-overview__avatar--lg bench-overview__avatar--photo"
                   [lazyImage]="entry.consultantId" [lazyImagePhotoId]="entry.photoId"
                   [alt]="entry.fullName"/>
            } @else {
              <span class="bench-overview__avatar bench-overview__avatar--lg">{{ getInitials(entry.fullName) }}</span>
            }
            <div class="bench-overview__card-person">
              <span class="bench-overview__card-name">{{ entry.fullName }}</span>
              @if (entry.abbreviation) {
                <span class="bench-overview__kuerzel">{{ entry.abbreviation }}</span>
              }
              <div class="bench-overview__card-badges">
                @for (category of categoriesOf(entry); track category) {
                  <span class="bench-overview__type-badge"
                        [ngClass]="'bench-overview__type-badge--' + category.toLowerCase()">
                    {{ ('benchOverview.type.' + category.toLowerCase()) | translate }}
                  </span>
                }
                @if (isBenchFirm(entry.firm)) {
                  <app-company-badge [abbreviation]="entry.firm"></app-company-badge>
                } @else {
                  <span class="bench-overview__empty">—</span>
                }
              </div>
            </div>
          </div>
          <div class="bench-overview__card-body">
            @if (entry.position) {
              <p class="bench-overview__card-row">{{ entry.position }}</p>
            }
            @if (entry.location) {
              <p class="bench-overview__card-row">
                <mat-icon class="bench-overview__pin-icon" fontIcon="location_on"></mat-icon>
                {{ entry.location }}
              </p>
            }
            <p class="bench-overview__card-row">
              {{ 'benchOverview.column.availableFrom' | translate }}:
              @if (entry.availableFrom) {
                <span class="bench-overview__avail"
                      [ngClass]="'bench-overview__avail--' + availabilityBucket(entry)">{{ entry.availableFrom | date: 'dd.MM.yyyy' }}</span>
              } @else {
                —
              }
            </p>
            @if (acceptedOfferDate(entry)) {
              <p class="bench-overview__card-row">
                {{ 'benchOverview.column.confirmedFrom' | translate }}: {{ acceptedOfferDate(entry) | date: 'dd.MM.yyyy' }}
              </p>
            }
          </div>
          <div class="bench-overview__card-footer">
            @let sum = offerSummary(entry);
            <div class="bench-overview__ang-mini bench-overview__ang-mini--card">
              <div class="bench-overview__ang-stats">
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--open]="sum.open > 0">
                  <b>{{ sum.open }}</b> {{ 'benchOverview.offersMini.open' | translate }}</span>
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--feedback]="sum.feedback > 0">
                  <b>{{ sum.feedback }}</b> {{ 'benchOverview.offersMini.feedback' | translate }}</span>
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--rejected]="sum.rejected > 0">
                  <b>{{ sum.rejected }}</b> {{ 'benchOverview.offersMini.rejected' | translate }}</span>
                <span class="bench-overview__ang-stat" [class.bench-overview__ang-stat--accepted]="sum.accepted > 0">
                  <b>{{ sum.accepted }}</b> {{ 'benchOverview.offersMini.accepted' | translate }}</span>
                <span class="bench-overview__ang-stat"
                      [class.bench-overview__ang-stat--project-created]="sum.projectCreated > 0">
                  <b>{{ sum.projectCreated }}</b> {{ 'benchOverview.offersMini.projectCreated' | translate }}</span>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
}

<app-sales-modal
  [visible]="salesModalOpen"
  [sales]="salesPersons"
  (visibleChange)="onSalesModalVisibleChange($event)"
  (saved)="onSalesSaved($event)">
</app-sales-modal>
